import { Audio } from "expo-av";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isSafari, isWeb } from "../TTS/helpers";

const TIMEOUT_DURATION_MS = 10000;

const UPDATE_INTERVAL = 100;

const usePlayer = (
	source: string | null,
	totalTimeInMilliseconds?: number,
	autoPlay?: boolean,
) => {
	const [sound, setSound] = useState<Audio.Sound | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(totalTimeInMilliseconds || 0);
	const [isBuffering, setIsBuffering] = useState(false);
	const [isLoaded, setIsLoaded] = useState(false);
	const [isEnded, setIsEnded] = useState(false);
	const [hasError, setHasError] = useState(false);

	const audioErrorTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);

	const loadSound = useCallback(async (source: string | null) => {
		if (!source) {
			return null;
		}
		const { sound, status } = await Audio.Sound.createAsync(
			{ uri: source },
			{
				progressUpdateIntervalMillis: UPDATE_INTERVAL, // Does not work on web
				positionMillis: 1, // Hack to ensure duration is fetched on web
			},
		);
		setIsLoaded(status.isLoaded);
		setSound(sound);

		return sound;
	}, []);

	const playSound = useCallback(async () => {
		let finalSound = sound;
		if (isSafari) {
			finalSound = await loadSound(source);
		}
		if (finalSound) {
			setIsPlaying(true);
			setIsEnded(false);
			await Audio.setAudioModeAsync({
				allowsRecordingIOS: false,
				playsInSilentModeIOS: true,
				playThroughEarpieceAndroid: false,
			});
			await finalSound.playAsync();
		}
	}, [source, loadSound, sound]);

	const pauseSound = useCallback(async () => {
		if (sound) {
			setIsPlaying(false);
			await sound.pauseAsync();
		}
	}, [sound]);

	const onSeek = useCallback(
		async (timeMs: number) => {
			if (sound) {
				await sound.setPositionAsync(timeMs);
				setIsEnded(false);
			}
		},
		[sound],
	);

	const unloadSound = useCallback(async () => {
		if (sound) {
			await sound.unloadAsync();
		}
	}, [sound]);

	// Load the sound
	useEffect(() => {
		loadSound(source);
	}, [source, loadSound]);

	// Update the states while the sound is playing
	useEffect(() => {
		if (!sound) {
			return;
		}
		sound.setOnPlaybackStatusUpdate((status) => {
			setIsLoaded(status.isLoaded);
			if (status.isLoaded) {
				setCurrentTime(status.positionMillis || 0);
				setIsBuffering(status.isBuffering);
				setIsPlaying(status.isPlaying);

				// * Duration is NaN for Audio Recorder on web
				if (
					!Number.isNaN(status.durationMillis) &&
					status.durationMillis !== Number.POSITIVE_INFINITY
					// * Audio Recorder does not produce duration metadata on web, leading to Infinity - https://arc.net/l/quote/osytlunc
				) {
					const hasEnded =
						status.didJustFinish ||
						Math.round(status.positionMillis) >=
							Math.round((status.durationMillis || 0) - 200);
					setIsEnded(hasEnded);
					setDuration(status.durationMillis || 0);
				}
			}
		});
	}, [sound]);

	// Ensures that the setOnPlaybackStatusUpdate is called every UPDATE_INTERVAL milliseconds on web
	useEffect(() => {
		if (!sound) {
			return;
		}
		if (!(isPlaying && isWeb)) {
			return;
		}

		const interval = setInterval(() => {
			sound.getStatusAsync();
		}, UPDATE_INTERVAL);

		return () => clearInterval(interval);
	}, [sound, isPlaying]);

	// If the audio doesn't begin to buffer within TIMEOUT_DURATION_MS, set the error state.
	useEffect(() => {
		if (!(isBuffering || isLoaded)) {
			audioErrorTimeout.current = setTimeout(() => {
				setHasError(true);
			}, TIMEOUT_DURATION_MS);

			return () => clearTimeout(audioErrorTimeout.current);
		}

		setHasError(false);
		clearTimeout(audioErrorTimeout.current);
	}, [isBuffering, isLoaded]);

	// For audio recorder, ensures that the duration is set to the totalTimeInMilliseconds
	useEffect(() => {
		if (totalTimeInMilliseconds) {
			setDuration(totalTimeInMilliseconds);
		}
	}, [totalTimeInMilliseconds]);

	// Once per source: on Safari, playSound reloads the sound, which would otherwise re-run this effect forever.
	const autoPlayedSource = useRef<string | null>(null);
	useEffect(() => {
		if (autoPlay && isLoaded && sound && autoPlayedSource.current !== source) {
			autoPlayedSource.current = source;
			playSound();
		}
	}, [autoPlay, isLoaded, sound, playSound, source]);

	// Unload the sound when the component unmounts
	useEffect(() => {
		return () => {
			unloadSound();
		};
	}, [unloadSound]);

	const returnValue = useMemo(
		() => ({
			playSound,
			pauseSound,
			isPlaying,
			currentTimeInMilliseconds: currentTime,
			totalTimeInMilliseconds: duration,
			isBuffering,
			onSeek,
			isEnded,
			isLoaded: isLoaded && sound,
			hasError,
			unloadSound,
		}),
		[
			playSound,
			pauseSound,
			isPlaying,
			currentTime,
			duration,
			isBuffering,
			onSeek,
			isEnded,
			isLoaded,
			hasError,
			unloadSound,
			sound,
		],
	);

	return returnValue;
};

export default usePlayer;
